import { useEffect, useState } from 'react';
import { Save, Plug, Loader } from 'lucide-react';
import {
  getDataSyncConfig,
  saveDataSyncConfig,
  testDataSyncConfig,
  DataSyncPublicConfig,
} from '../../../utils/dataSyncApi';

export function ConfigSyncTab() {
  const [config, setConfig] = useState<DataSyncPublicConfig | null>(null);
  const [projectId, setProjectId] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [storageBucket, setStorageBucket] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await getDataSyncConfig();
        if (cancelled) return;
        setConfig(cfg);
        setProjectId(cfg.projectId || '');
        setStorageBucket(cfg.storageBucket || '');
        // clientEmail is masked — leave empty for edit; user re-enters if changing
        setClientEmail('');
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const body: {
        projectId: string;
        clientEmail?: string;
        privateKey?: string;
        storageBucket?: string;
      } = {
        projectId: projectId.trim(),
        storageBucket: storageBucket.trim() || undefined,
      };

      if (clientEmail.trim()) {
        body.clientEmail = clientEmail.trim();
      } else if (!config?.configured) {
        setError('clientEmail requis');
        setSaving(false);
        return;
      }

      if (privateKey.trim()) {
        body.privateKey = privateKey.trim();
      } else if (!config?.hasPrivateKey) {
        setError('privateKey requise');
        setSaving(false);
        return;
      }

      const saved = await saveDataSyncConfig(body);
      setConfig(saved);
      setPrivateKey('');
      setClientEmail('');
      setMessage('Configuration enregistrée.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError(null);
    setMessage(null);
    try {
      const result = await testDataSyncConfig();
      if (result.ok) {
        setMessage(
          `Connexion OK — projet ${result.projectId || '?'} (${result.collectionsCount ?? 0} collections).`
        );
      } else {
        setError(result.error || 'Échec du test');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-menioo-green" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h3 className="text-lg font-display font-bold text-menioo-navy">
          Base destination (préprod)
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Credentials Firebase Admin de la base cible. La private key n’est jamais renvoyée après
          sauvegarde.
        </p>
        {config?.configured && (
          <p className="text-sm text-menioo-green mt-2">
            Configurée : {config.projectId} ({config.clientEmail})
            {config.hasPrivateKey ? ' — clé présente' : ''}
          </p>
        )}
      </div>

      {message && (
        <div className="rounded-md bg-green-50 border border-green-200 text-green-800 px-4 py-3 text-sm">
          {message}
        </div>
      )}
      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-4 bg-white rounded-lg border border-gray-200 p-6">
        <div>
          <label className="block text-sm font-medium text-menioo-navy">Project ID</label>
          <input
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-menioo-green focus:ring-menioo-green sm:text-sm"
            placeholder="menioo-blue"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-menioo-navy">Client email</label>
          <input
            value={clientEmail}
            onChange={(e) => setClientEmail(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-menioo-green focus:ring-menioo-green sm:text-sm"
            placeholder="firebase-adminsdk@….iam.gserviceaccount.com"
          />
          {config?.clientEmail && (
            <p className="text-xs text-gray-400 mt-1">Actuel (masqué) : {config.clientEmail}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-menioo-navy">
            Private key {config?.hasPrivateKey ? '(laisser vide pour conserver)' : ''}
          </label>
          <textarea
            value={privateKey}
            onChange={(e) => setPrivateKey(e.target.value)}
            rows={5}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-menioo-green focus:ring-menioo-green sm:text-sm font-mono text-xs"
            placeholder="-----BEGIN PRIVATE KEY-----&#10;…&#10;-----END PRIVATE KEY-----"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-menioo-navy">Storage bucket (optionnel)</label>
          <input
            value={storageBucket}
            onChange={(e) => setStorageBucket(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-menioo-green focus:ring-menioo-green sm:text-sm"
            placeholder="menioo-blue.appspot.com"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-menioo-green hover:bg-menioo-green-dark disabled:opacity-50"
          >
            {saving ? <Loader className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Enregistrer
          </button>
          <button
            type="button"
            onClick={handleTest}
            disabled={testing || !config?.configured}
            className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-menioo-navy bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
          >
            {testing ? <Loader className="h-4 w-4 mr-2 animate-spin" /> : <Plug className="h-4 w-4 mr-2" />}
            Tester la connexion
          </button>
        </div>
      </form>
    </div>
  );
}
